/**
 * Справочник пестицидов — TanStack Query hooks (AK-385).
 *
 * Пользовательская часть (read-only, любой аутентиф.) + системная админка
 * (platform_admin). Все запросы через общий `api`-клиент; ключи в
 * `pesticideKeys`. Real-backend всегда (мок-режима у пестицидов нет — контракт
 * едет из `feat/ak-385-backend`).
 *
 * Пути и поля ответов — строго по `api/v1/reference/pesticides.py`.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  CodeNamePayload,
  PagedResponse,
  ProductCard,
  ProductListItem,
  ProductListQuery,
  QueueItem,
  QueueResolvePayload,
  RegistrantPayload,
  ReverseGroup,
  ReverseSearchQuery,
  SubCatalogItem,
  SubstanceItem,
  SubstancePayload,
  TreatedObjectPayload,
  UnitItem,
} from './types';

const BASE = '/api/v1/reference/pesticides';

export const pesticideKeys = {
  all: ['pesticides'] as const,
  products: (q: ProductListQuery) => ['pesticides', 'products', q] as const,
  product: (id: string) => ['pesticides', 'product', id] as const,
  analogs: (id: string, strict: boolean) =>
    ['pesticides', 'analogs', id, strict] as const,
  reverse: (q: ReverseSearchQuery) => ['pesticides', 'reverse', q] as const,
  substances: (q: string) => ['pesticides', 'substances', q] as const,
  substanceProducts: (id: string, status: string | null | undefined) =>
    ['pesticides', 'substance-products', id, status ?? 'all'] as const,
  subCatalog: (kind: string, includeArchived: boolean, q?: string) =>
    ['pesticides', 'sub', kind, includeArchived, q ?? ''] as const,
  units: () => ['pesticides', 'units'] as const,
  queue: (termKind: string | undefined, status: string) =>
    ['pesticides', 'queue', termKind ?? 'all', status] as const,
};

function buildParams(q: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

// ═══════════════════════════════════════════════════════════════════════════
// READ — products by name
// ═══════════════════════════════════════════════════════════════════════════

export function useProducts(
  query: ProductListQuery,
  enabled = true,
): UseQueryResult<PagedResponse<ProductListItem>, Error> {
  return useQuery({
    queryKey: pesticideKeys.products(query),
    queryFn: () =>
      api.get<PagedResponse<ProductListItem>>(
        `${BASE}/products${buildParams({
          q: query.q,
          status: query.status,
          limit: query.limit ?? 50,
          offset: query.offset ?? 0,
        })}`,
      ),
    enabled,
    staleTime: 30_000,
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// READ — product card
// ═══════════════════════════════════════════════════════════════════════════

export function useProduct(
  productId: string | null,
): UseQueryResult<ProductCard, Error> {
  return useQuery({
    queryKey: pesticideKeys.product(productId ?? ''),
    queryFn: () => api.get<ProductCard>(`${BASE}/products/${productId}`),
    enabled: Boolean(productId),
    staleTime: 30_000,
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// READ — analogs (strict / wide)
// ═══════════════════════════════════════════════════════════════════════════

export function useAnalogs(
  productId: string | null,
  strict: boolean,
): UseQueryResult<ProductListItem[], Error> {
  return useQuery({
    queryKey: pesticideKeys.analogs(productId ?? '', strict),
    queryFn: () =>
      api.get<ProductListItem[]>(
        `${BASE}/products/${productId}/analogs${buildParams({ strict })}`,
      ),
    enabled: Boolean(productId),
    staleTime: 30_000,
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// READ — reverse search (culture + harmful object + registrant)
// ═══════════════════════════════════════════════════════════════════════════

/** Есть ли хоть один заданный фильтр (backend требует ≥1). */
export function reverseSearchReady(q: ReverseSearchQuery): boolean {
  return Boolean(q.crop_id || q.object_ref_id || q.treated_object_id);
}

export function useReverseSearch(
  query: ReverseSearchQuery,
): UseQueryResult<ReverseGroup[], Error> {
  return useQuery({
    queryKey: pesticideKeys.reverse(query),
    queryFn: () =>
      api.get<ReverseGroup[]>(
        `${BASE}/search${buildParams({
          crop_id: query.crop_id,
          object_kind: query.object_kind,
          object_ref_id: query.object_ref_id,
          treated_object_id: query.treated_object_id,
          registrant_id: query.registrant_id,
          status: query.status,
        })}`,
      ),
    enabled: reverseSearchReady(query),
    staleTime: 30_000,
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// READ — substances + products by substance
// ═══════════════════════════════════════════════════════════════════════════

export function useSubstancesSearch(
  q: string,
  enabled = true,
): UseQueryResult<PagedResponse<SubstanceItem>, Error> {
  return useQuery({
    queryKey: pesticideKeys.substances(q),
    queryFn: () =>
      api.get<PagedResponse<SubstanceItem>>(
        `${BASE}/substances${buildParams({ q, limit: 50 })}`,
      ),
    enabled,
    staleTime: 30_000,
  });
}

export function useProductsBySubstance(
  substanceId: string | null,
  status: string | null | undefined = 'active',
): UseQueryResult<ProductListItem[], Error> {
  return useQuery({
    queryKey: pesticideKeys.substanceProducts(substanceId ?? '', status),
    queryFn: () =>
      api.get<ProductListItem[]>(
        `${BASE}/substances/${substanceId}/products${buildParams({ status })}`,
      ),
    enabled: Boolean(substanceId),
    staleTime: 30_000,
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// READ — sub-catalog lists (shared by user filters + admin)
// ═══════════════════════════════════════════════════════════════════════════

type CodeNameCatalog =
  | 'categories'
  | 'formulations'
  | 'treated-objects'
  | 'registrants';

export function useSubCatalog(
  kind: CodeNameCatalog,
  // `enabled` — для экранов, где список нужен не всегда (например, назначения
  // СЗР в бюджетной позиции: у ГСМ и удобрений такого каталога нет и дёргать
  // эндпоинт незачем). Хук нельзя вызвать условно, а запрос — можно.
  opts: { includeArchived?: boolean; q?: string; enabled?: boolean } = {},
): UseQueryResult<SubCatalogItem[], Error> {
  const includeArchived = opts.includeArchived ?? false;
  return useQuery({
    queryKey: pesticideKeys.subCatalog(kind, includeArchived, opts.q),
    queryFn: () =>
      api.get<SubCatalogItem[]>(
        `${BASE}/${kind}${buildParams({
          include_archived: includeArchived,
          q: opts.q,
        })}`,
      ),
    enabled: opts.enabled ?? true,
    staleTime: 60_000,
  });
}

export function useUnits(): UseQueryResult<UnitItem[], Error> {
  return useQuery({
    queryKey: pesticideKeys.units(),
    queryFn: () => api.get<UnitItem[]>(`${BASE}/units`),
    staleTime: 5 * 60_000,
  });
}

export function useSubstancesList(
  opts: { q?: string } = {},
): UseQueryResult<PagedResponse<SubstanceItem>, Error> {
  return useQuery({
    queryKey: pesticideKeys.substances(opts.q ?? ''),
    queryFn: () =>
      api.get<PagedResponse<SubstanceItem>>(
        `${BASE}/substances${buildParams({ q: opts.q, limit: 500 })}`,
      ),
    staleTime: 60_000,
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// ADMIN — import queue
// ═══════════════════════════════════════════════════════════════════════════

export function useImportQueue(
  termKind: string | undefined,
  status = 'pending',
): UseQueryResult<QueueItem[], Error> {
  return useQuery({
    queryKey: pesticideKeys.queue(termKind, status),
    queryFn: () =>
      api.get<QueueItem[]>(
        `${BASE}/import/queue${buildParams({
          term_kind: termKind,
          status,
          limit: 200,
        })}`,
      ),
    staleTime: 15_000,
  });
}

export function useResolveQueueItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      queueId,
      payload,
    }: {
      queueId: string;
      payload: QueueResolvePayload;
    }) => api.post<void>(`${BASE}/import/queue/${queueId}/resolve`, payload),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['pesticides', 'queue'] }),
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// ADMIN — sub-catalog CRUD (create + archive)
// ═══════════════════════════════════════════════════════════════════════════

function invalidateSub(qc: ReturnType<typeof useQueryClient>, kind: string) {
  qc.invalidateQueries({ queryKey: ['pesticides', 'sub', kind] });
}

export function useCreateCodeName(kind: CodeNameCatalog) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CodeNamePayload) =>
      api.post<SubCatalogItem>(`${BASE}/${kind}`, payload),
    onSuccess: () => invalidateSub(qc, kind),
  });
}

export function useArchiveSubItem(kind: CodeNameCatalog) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`${BASE}/${kind}/${id}`),
    onSuccess: () => invalidateSub(qc, kind),
  });
}

export function useCreateRegistrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RegistrantPayload) =>
      api.post<SubCatalogItem>(`${BASE}/registrants`, payload),
    onSuccess: () => invalidateSub(qc, 'registrants'),
  });
}

export function useArchiveRegistrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`${BASE}/registrants/${id}`),
    onSuccess: () => invalidateSub(qc, 'registrants'),
  });
}

export function useCreateTreatedObject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: TreatedObjectPayload) =>
      api.post<SubCatalogItem>(`${BASE}/treated-objects`, payload),
    onSuccess: () => invalidateSub(qc, 'treated-objects'),
  });
}

export function useArchiveTreatedObject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`${BASE}/treated-objects/${id}`),
    onSuccess: () => invalidateSub(qc, 'treated-objects'),
  });
}

export function useCreateSubstance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SubstancePayload) =>
      api.post<SubstanceItem>(`${BASE}/substances`, payload),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['pesticides', 'substances'] }),
  });
}

export function useArchiveSubstance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`${BASE}/substances/${id}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['pesticides', 'substances'] }),
  });
}
